/**
 * ปฏิทินเวรทั้งทีม — แกนตั้ง = พนักงาน, แกนนอน = วันที่ในช่วงที่เลือก.
 *
 * ดึงทุกอย่างจาก GET /shift/roster-view ครั้งเดียว (คน + เวร + กะ + วันหยุด +
 * วันลา + วันหยุดประจำสัปดาห์) แล้ว group ใน client — ไม่ยิง API ต่อคน.
 * คอลัมน์ชื่อพนักงาน sticky ซ้าย, ตัวตารางเลื่อนแนวนอนได้.
 *
 * ขอบเขตคนที่เห็น backend เป็นคนตัดสิน (พนักงาน = แผนกตัวเอง · หัวหน้างานขึ้นไป
 * = ทุกคนทุกแผนก) — หน้าจอไม่กรองสิทธิ์เอง มีแต่ตัวกรองเพื่อ "ดูให้แคบลง".
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useRosterView, ROSTER_VIEW_MAX_DAYS,
  type RosterViewEmployee, type RosterViewData,
} from '../../hooks';
import { Loading } from '../../components/layout/Loading';
import { MultiSelect } from '../../components/ui/MultiSelect';
import {
  DateRangePicker, eachDay, isoOf, startOfMonthIso, endOfMonthIso, countDays, thaiShortDate,
  type DateRange,
} from '../../components/ui/DateRangePicker';

const THAI_DOW = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']; // index = JS getDay (0=Sun)
const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

// โทน Soft Gradient — ชมพู/พีช/เหลือง/เขียวอ่อน/ฟ้าอ่อน/ม่วงอ่อน วนกัน
const SHIFT_COLORS = [
  'bg-pink-100 text-pink-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-orange-100 text-orange-700',
];

const LEAVE_LABEL: Record<string, string> = {
  sick: 'ลาป่วย', personal: 'ลากิจ', vacation: 'ลาพักร้อน', maternity: 'ลาคลอด', ordination: 'ลาบวช',
};

function empLabel(e: RosterViewEmployee): string {
  return e.nickname ? `${e.name} (${e.nickname})` : e.name;
}

export function HRRosterCalendarContent() {
  const [range, setRange] = useState<DateRange>(() => ({ from: startOfMonthIso(), to: endOfMonthIso() }));
  const [deptFilter, setDeptFilter] = useState<string[]>([]);
  const [positionFilter, setPositionFilter] = useState<string[]>([]);

  const { data, isLoading, error } = useRosterView(range.from, range.to);

  const days = useMemo(() => eachDay(range.from, range.to), [range.from, range.to]);
  const dayCount = countDays(range.from, range.to);

  // ตัวเลือกตัวกรองมาจาก "คนที่เห็นได้จริง" — ไม่ใช่ทะเบียนแผนกทั้งบริษัท
  // (ไม่งั้นพนักงานจะเห็นชื่อแผนกที่ตัวเองเข้าไม่ถึงในลิสต์)
  const deptOptions = useMemo(
    () => [...new Set((data?.employees ?? []).map((e) => (e.department ?? '').trim()).filter(Boolean))].sort(),
    [data],
  );
  const positionOptions = useMemo(() => {
    const pool = (data?.employees ?? []).filter(
      (e) => deptFilter.length === 0 || deptFilter.includes((e.department ?? '').trim()),
    );
    return [...new Set(pool.map((e) => (e.position ?? '').trim()).filter(Boolean))].sort();
  }, [data, deptFilter]);

  // เปลี่ยนแผนกแล้วตำแหน่งที่เลือกไว้อาจไม่มีในแผนกใหม่ → ตัดทิ้งเฉพาะตัวที่หลุด
  // (ไม่ล้างทั้งหมด เพื่อไม่ให้เสียตัวกรองที่ยังใช้ได้)
  useEffect(() => {
    setPositionFilter((cur) => cur.filter((p) => positionOptions.includes(p)));
    // positionOptions เปลี่ยนตาม deptFilter เท่านั้น
  }, [positionOptions.join('|')]);

  const shiftById = useMemo(() => {
    const m = new Map<string, { code: string; name: string; start_time: string; end_time: string; color: string }>();
    (data?.shifts ?? []).forEach((s, i) => {
      m.set(s.id, { ...s, color: SHIFT_COLORS[i % SHIFT_COLORS.length]! });
    });
    return m;
  }, [data]);

  // emp_code|date → shift_id (มีแถว = "กำหนดแล้ว", shift_id null = หยุดจริง)
  const rosterByKey = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const r of data?.roster ?? []) m.set(`${r.emp_code}|${r.date}`, r.shift_id);
    return m;
  }, [data]);

  const leaveByKey = useMemo(() => {
    const m = new Map<string, string | undefined>();
    for (const l of data?.leaves ?? []) m.set(`${l.emp_code}|${l.date}`, l.leave_type);
    return m;
  }, [data]);

  const weekendIso = useMemo(() => new Set<number>(data?.weekend_days ?? [7]), [data]);
  // บังคับทำงานวันหยุดประเพณี → ไม่แสดงวันหยุดในปฏิทิน
  const holidayByDate = useMemo(() => {
    const m = new Map<string, string>();
    if (!data?.holiday_as_workday) for (const h of data?.holidays ?? []) m.set(h.date, h.name);
    return m;
  }, [data]);

  const list = useMemo(() => {
    return (data?.employees ?? []).filter((e) => {
      if (!e.emp_code) return false;
      if (deptFilter.length && !deptFilter.includes((e.department ?? '').trim())) return false;
      if (positionFilter.length && !positionFilter.includes((e.position ?? '').trim())) return false;
      return true;
    });
  }, [data, deptFilter, positionFilter]);

  // legend เฉพาะกะที่ปรากฏในช่วงนี้
  const usedShiftIds = useMemo(() => {
    const s = new Set<string>();
    for (const r of data?.roster ?? []) if (r.shift_id) s.add(r.shift_id);
    return s;
  }, [data]);

  function isWeekendDay(d: Date): boolean {
    const iso = d.getDay() === 0 ? 7 : d.getDay(); // 1=Mon..7=Sun
    return weekendIso.has(iso);
  }

  return (
    <div className="space-y-3">
      {/* ช่วงวันที่ + ตัวกรอง (เลือกได้หลายแผนก/หลายตำแหน่ง) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-3xl">
        <DateRangePicker
          label="ช่วงวันที่"
          value={range}
          onChange={setRange}
          maxDays={ROSTER_VIEW_MAX_DAYS}
        />
        <MultiSelect
          label="แผนก"
          options={deptOptions}
          value={deptFilter}
          onChange={setDeptFilter}
          allLabel="ทุกแผนก"
        />
        <MultiSelect
          label="ตำแหน่ง"
          options={positionOptions}
          value={positionFilter}
          onChange={setPositionFilter}
          allLabel="ทุกตำแหน่ง"
        />
      </div>

      <div className="text-xs text-warm-gray">
        {thaiShortDate(range.from)} – {thaiShortDate(range.to)} · {dayCount} วัน
        {data ? ` · แสดง ${list.length} คน จาก ${data.employees.length} คน (${data.scope_label})` : ''}
      </div>

      {/* ตารางทั้งทีม */}
      <div className="card p-0 overflow-x-auto">
        {isLoading ? (
          <Loading />
        ) : error ? (
          <p className="text-center text-red-600 py-10 text-sm">
            {error instanceof Error ? error.message : 'โหลดตารางเวรไม่สำเร็จ'}
          </p>
        ) : list.length === 0 ? (
          <p className="text-center text-warm-gray py-10 text-sm">
            {(data?.employees.length ?? 0) === 0 ? 'ยังไม่มีพนักงาน' : 'ไม่มีคนตรงกับตัวกรองที่เลือก'}
          </p>
        ) : (
          <table className="border-collapse text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-paper border-b border-r border-gray-200 px-3 py-2 text-left font-medium text-warm-gray min-w-[150px]">
                  พนักงาน
                </th>
                {days.map((d) => {
                  const iso = isoOf(d);
                  const we = isWeekendDay(d);
                  const ho = holidayByDate.has(iso);
                  return (
                    <th
                      key={iso}
                      className={`border-b border-gray-100 px-1 py-1 text-center font-medium min-w-[34px] ${
                        ho ? 'bg-red-50 text-red-500' : we ? 'bg-gray-50 text-gray-400' : 'text-warm-gray'
                      }`}
                      title={ho ? holidayByDate.get(iso) : undefined}
                    >
                      <div className="leading-tight">{d.getDate()}</div>
                      <div className="text-[9px] leading-tight">{THAI_DOW[d.getDay()]}</div>
                      {/* ข้ามเดือน → บอกเดือนกำกับวันที่ 1 กันอ่านสับสน */}
                      {d.getDate() === 1 && (
                        <div className="text-[8px] leading-tight text-warm-gray whitespace-nowrap">
                          {THAI_MONTHS_SHORT[d.getMonth()]}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.emp_code} className="hover:bg-gray-50/50">
                  <td className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-3 py-1.5 align-middle min-w-[150px]">
                    <div className="text-ink truncate max-w-[140px]">{empLabel(e)}</div>
                    <div className="text-[10px] text-warm-gray font-mono">{e.emp_code}</div>
                  </td>
                  {days.map((d) => {
                    const iso = isoOf(d);
                    const we = isWeekendDay(d);
                    const ho = holidayByDate.has(iso);
                    const key = `${e.emp_code}|${iso}`;
                    const hasRow = rosterByKey.has(key);
                    const shiftId = rosterByKey.get(key) ?? null;
                    const shift = shiftId ? shiftById.get(shiftId) : null;
                    const onLeave = leaveByKey.has(key);
                    const leaveType = leaveByKey.get(key);

                    // ลาอนุมัติแล้ว → ป้าย "ลา" (กันลงเวรทับ)
                    // มีกะ → โค้ดกะ/สี
                    // ลงไว้ว่าหยุด → ป้ายแดง "หยุด" (เห็นชัดว่าวันนั้นไม่ต้องมา)
                    // ยังไม่ได้ลงตาราง → ขีดจาง (คนละเรื่องกับ "หยุด")
                    let inner;
                    if (onLeave) {
                      inner = (
                        <span className={`inline-block rounded px-1 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-700 whitespace-nowrap ${shift ? 'ring-1 ring-red-400' : ''}`}>
                          ลา
                        </span>
                      );
                    } else if (shift) {
                      inner = (
                        <span className={`inline-block rounded px-1 py-0.5 text-[10px] font-medium whitespace-nowrap ${shift.color}`}>
                          {shift.code}
                        </span>
                      );
                    } else if (hasRow) {
                      inner = (
                        <span className="inline-block rounded px-1 py-0.5 text-[10px] font-semibold bg-red-500 text-white whitespace-nowrap">
                          หยุด
                        </span>
                      );
                    } else {
                      inner = <span className="inline-block text-[10px] text-gray-300 leading-none">—</span>;
                    }

                    const title = onLeave
                      ? `${e.name} · ${thaiShortDate(iso)} · ${data?.can_see_leave_type ? (LEAVE_LABEL[leaveType ?? ''] ?? 'ลา') : 'ลา'} (อนุมัติแล้ว)${shift ? ' — มีกะทำงานทับวันลา' : ''}`
                      : shift
                        ? `${e.name} · ${thaiShortDate(iso)} · ${shift.name} (${shift.start_time}–${shift.end_time})`
                        : hasRow
                          ? `${e.name} · ${thaiShortDate(iso)} · วันหยุด`
                          : `${e.name} · ${thaiShortDate(iso)} · ยังไม่ได้ลงตาราง`;

                    return (
                      <td
                        key={iso}
                        className={`border-b border-gray-50 text-center px-0.5 py-1 ${
                          ho ? 'bg-red-50/40' : we ? 'bg-gray-50/60' : ''
                        }`}
                        title={title}
                      >
                        {inner}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* legend */}
      <div className="flex items-center gap-3 flex-wrap text-[11px] text-warm-gray">
        {(data?.shifts ?? [])
          .filter((s) => usedShiftIds.has(s.id))
          .map((s) => (
            <span key={s.id} className="flex items-center gap-1 whitespace-nowrap">
              <span className={`inline-block rounded px-1 py-0.5 text-[10px] font-medium ${shiftById.get(s.id)?.color ?? ''}`}>
                {s.code}
              </span>
              {s.name}
            </span>
          ))}
        <span className="flex items-center gap-1 whitespace-nowrap">
          <span className="inline-block rounded px-1 py-0.5 text-[10px] font-semibold bg-red-500 text-white">หยุด</span> วันหยุดของคนนั้น
        </span>
        <span className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-gray-300">—</span> ยังไม่ได้ลงตาราง
        </span>
        <span className="flex items-center gap-1 whitespace-nowrap">
          <span className="inline-block rounded px-1 text-[10px] font-medium bg-rose-100 text-rose-700">ลา</span> ลาอนุมัติแล้ว (ลงเวรทับไม่ได้)
        </span>
        <span className="flex items-center gap-1 whitespace-nowrap">
          <span className="inline-block w-3 h-3 rounded bg-red-50 border border-red-200" /> วันหยุดบริษัท
        </span>
        <span className="flex items-center gap-1 whitespace-nowrap">
          <span className="inline-block w-3 h-3 rounded bg-gray-50 border border-gray-200" /> หยุดประจำสัปดาห์
        </span>
      </div>
      <p className="text-[11px] text-warm-gray">
        เลื่อนตารางแนวนอนเพื่อดูทั้งช่วง · แตะช่องเพื่อดูรายละเอียดกะ · เลือกช่วงได้ครั้งละไม่เกิน {ROSTER_VIEW_MAX_DAYS} วัน
      </p>
    </div>
  );
}

/** ใช้ซ้ำในหน้าพนักงาน — ตารางเดียวกัน แต่บอกด้วยว่ากำลังดูของทีมไหน */
export type { RosterViewData };
